import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { useOrganizationId } from '@/contexts/OrganizationContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';

export type GetUserByIdResponse = components['schemas']['GetUserByIdEndpoint_GetUserByIdResponse'];

export interface UseUserByIdReturn extends BaseQuery {
  user: GetUserByIdResponse | null;
}

export function useUserById(userId: string | undefined): UseUserByIdReturn {
  const { organizationId } = useOrganizationId();
  const { getAccessToken } = useAuth();

  const getUserById = async () => {
    if (!userId) {
      return null;
    }

    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/users/{userId}', {
      params: {
        path: { organizationId, userId },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetUserByIdResponse;
  };

  const query = useQuery({
    queryKey: ['userById', organizationId, userId],
    queryFn: getUserById,
    enabled: !!userId && organizationId > 0,
    retry: false,
  });

  const { data, isLoading, error, refetch, isRefetching } = query;

  return {
    user: data ?? null,
    isLoading,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
